import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import CircularProgress from '@material-ui/core/CircularProgress'
import { i18n } from '../../translate/i18n'
import api from '../../services/api'
import ButtonWithSpinner from '../ButtonWithSpinner'
import ContactModal from '../ContactModal'
import toastError from '../../errors/toastError'
import { AuthContext } from '../../context/Auth/AuthContext'
import { Grid, ListItemText, MenuItem, Select } from '@material-ui/core'
import { toast } from 'react-toastify'
import TicketAlreadyOpenModal from '../TicketAlreadyOpenModal'

const useStyles = makeStyles(theme => ({
  online: {
    fontSize: 11,
    color: '#25d366',
  },
  offline: {
    fontSize: 11,
    color: '#e1306c',
  },
}))

const filter = createFilterOptions({
  trim: true,
})
const isGroupQueue = queue => queue?.name?.trim().toLowerCase() === 'grupos'

const findDefaultQueueId = queues => {
  if (!Array.isArray(queues) || queues.length === 0) return ''

  const firstNonGroupQueue = queues.find(queue => !isGroupQueue(queue))

  return (firstNonGroupQueue || queues[0])?.id || ''
}

const findDefaultWhatsappId = (whatsapps, preferredId) => {
  if (preferredId && Array.isArray(whatsapps) && whatsapps.some(whatsapp => whatsapp.id === preferredId)) {
    return preferredId
  }

  if (Array.isArray(whatsapps) && whatsapps.length > 0) {
    return whatsapps[0].id
  }

  return ''
}

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles()
  const history = useHistory()
  const [options, setOptions] = useState([])
  const [, setChannelFilter] = useState(null)

  const [loading, setLoading] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedQueue, setSelectedQueue] = useState('')
  const [selectedWhatsapp, setSelectedWhatsapp] = useState('')
  const [newContact, setNewContact] = useState({})
  const [whatsapps, setWhatsapps] = useState([])
  const [defaultWhatsappId, setDefaultWhatsappId] = useState(null)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const { user } = useContext(AuthContext)
  const { companyId, whatsappId } = user
  const userQueues = useMemo(() => {
    if (!Array.isArray(user?.queues)) return []
    return user.queues.filter(queue => !isGroupQueue(queue))
  }, [user?.queues])

  // Estado para controlar a exibição do modal de ticket já existente
  const [openExistingTicketModal, setOpenExistingTicketModal] = useState(false)
  const [existingTicket, setExistingTicket] = useState(null)
  const [attendingUserName, setAttendingUserName] = useState('')

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact])
      setSelectedContact(initialContact)
    }
  }, [initialContact])

  useEffect(() => {
    if (!modalOpen) return

    let isActive = true
    setLoading(true)

    const loadWhatsapps = async () => {
      try {
        const [userResponse, whatsappResponse] = await Promise.allSettled([api.get('/auth/me'), api.get(`/whatsapp`, { params: { companyId, session: 0 } })])

        if (!isActive) return

        const preferredWhatsappId = userResponse.status === 'fulfilled' ? userResponse.value?.data?.defaultWhatsapp?.id || whatsappId : whatsappId
        const allowedWhatsapps = userResponse.status === 'fulfilled' ? userResponse.value?.data?.whatsapps || [] : user.whatsapps || []
        const allowedWhatsappIds = allowedWhatsapps.map(whatsapp => whatsapp.id)

        if (userResponse.status === 'rejected') {
          toastError(userResponse.reason)
        } else {
          setDefaultWhatsappId(preferredWhatsappId || null)
        }

        if (whatsappResponse.status === 'fulfilled') {
          const data = allowedWhatsappIds.length > 0 ? whatsappResponse.value.data.filter(whatsapp => allowedWhatsappIds.includes(whatsapp.id)) : whatsappResponse.value.data
          setWhatsapps(data)
          setSelectedWhatsapp(findDefaultWhatsappId(data, preferredWhatsappId))
        } else {
          toastError(whatsappResponse.reason)
          setWhatsapps([])
          setSelectedWhatsapp('')
        }
      } catch (err) {
        if (!isActive) return
        toastError(err)
        setWhatsapps([])
        setSelectedWhatsapp('')
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    setSelectedQueue(findDefaultQueueId(userQueues))
    loadWhatsapps()

    return () => {
      isActive = false
      setLoading(false)
    }
  }, [modalOpen, companyId, userQueues, whatsappId, user.whatsapps])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false)
      return
    }
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get('contacts', {
            params: { searchParam },
          })
          setOptions(data.contacts)
          setLoading(false)
        } catch (err) {
          setLoading(false)
          toastError(err)
        }
      }
      fetchContacts()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, modalOpen])

  const handleClose = () => {
    onClose()
    setSearchParam('')
    setOpenExistingTicketModal(false)
    setExistingTicket(null)
    setSelectedContact(null)
    setSelectedQueue(findDefaultQueueId(userQueues))
    setSelectedWhatsapp(findDefaultWhatsappId(whatsapps, defaultWhatsappId || whatsappId))
  }

  const handleCloseExistingTicketModal = () => {
    setOpenExistingTicketModal(false)
    setExistingTicket(null)
    setAttendingUserName('')
    setLoading(false)
  }

  const handleSaveTicket = async contactId => {
    if (!contactId) return
    if (selectedQueue === '') {
      toast.error('Selecione uma fila')
      return
    }
    setLoading(true)
    try {
      const queueId = selectedQueue !== '' ? selectedQueue : null
      const whatsappId = selectedWhatsapp !== '' ? selectedWhatsapp : null

      const response = await api.post('/tickets', {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: 'open',
      })

      const data = response.data

      // Se o ticket foi criado com sucesso
      if (data && !data.error) {
        onClose(data)
        return
      }

      // Se já existe um ticket
      if (data && data.error === true && data.type === 'TICKET_ALREADY_EXISTS') {
        setExistingTicket(data.ticket)
        setOpenExistingTicketModal(true)
        return
      }
    } catch (err) {
      // Tratamento de erros da API
      const errorMessage = err?.response?.data?.error || err?.message || ''

      // Verificar se é erro de ticket já aberto
      if (errorMessage.includes('TICKET_ALREADY_OPEN')) {
        const parts = errorMessage.split('|')
        if (parts.length >= 2) {
          setAttendingUserName(parts[1] || 'Nenhum atendente')
          setOpenExistingTicketModal(true)
        } else {
          setAttendingUserName('Nenhum atendente')
          setOpenExistingTicketModal(true)
        }
        return
      }

      // Verificar se já existe um ticket (formato antigo)
      if (err.response?.data?.error === true && err.response?.data?.type === 'TICKET_ALREADY_EXISTS') {
        setExistingTicket(err.response.data.ticket)
        setOpenExistingTicketModal(true)
        return
      }

      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue)
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name })
      setContactModalOpen(true)
    }
  }

  const handleCloseContactModal = () => {
    setContactModalOpen(false)
  }

  const handleAddNewContactTicket = contact => {
    setSelectedContact(contact)
  }

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params)
    if (params.inputValue !== '' && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      })
    }
    return filtered
  }

  const renderOption = option => {
    if (option.number) {
      return (
        <>
          {/* {IconChannel(option.channel)} */}
          <Typography component='span' style={{ fontSize: 14, marginLeft: '10px', display: 'inline-flex', alignItems: 'center', lineHeight: '2' }}>
            {option.name} - {option.number}
          </Typography>
        </>
      )
    } else {
      return `${i18n.t('newTicketModal.add')} ${option.name}`
    }
  }

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`
    } else {
      return `${option.name}`
    }
  }

  const renderContactAutocomplete = () => {
    if (!initialContact?.id) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {
              setChannelFilter(newValue ? newValue.channel : 'whatsapp')
              handleSelectOption(e, newValue)
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t('newTicketModal.fieldLabel')}
                variant='outlined'
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return
                  else if (e.key === 'Enter') {
                    handleSaveTicket(selectedContact.id)
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )
    }
    return null
  }

  const navigateToExistingTicket = () => {
    if (existingTicket) {
      history.push(`/tickets/${existingTicket.id}`)
      handleCloseExistingTicketModal()
      handleClose()
    }
  }

  return (
    <>
      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id='form-dialog-title'>{i18n.t('newTicketModal.title')}</DialogTitle>
        <DialogContent dividers>
          <Grid style={{ width: 300 }} container spacing={2}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {/* FILA */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant='outlined'
                value={selectedQueue}
                onChange={e => {
                  setSelectedQueue(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === '') {
                    return 'Selecione uma fila'
                  }
                  const queue = user.queues.find(q => q.id === selectedQueue)
                  return queue.name
                }}
              >
                {user.queues?.length > 0 &&
                  user.queues.map((queue, key) => (
                    <MenuItem dense key={key} value={queue.id} translate='no'>
                      <ListItemText translate='no' primary={queue.name} />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            {/* CONEXAO */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant='outlined'
                value={selectedWhatsapp}
                onChange={e => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === '') {
                    return 'Selecione uma Conexão'
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                  return whatsapp.name
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <ListItemText
                        primary={
                          <>
                            <Typography component='span' style={{ fontSize: 14, marginLeft: '10px', display: 'inline-flex', alignItems: 'center', lineHeight: '2' }}>
                              {whatsapp.name} &nbsp; <p className={whatsapp.status === 'CONNECTED' ? classes.online : classes.offline}>({whatsapp.status})</p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='secondary' disabled={loading} variant='outlined'>
            {i18n.t('newTicketModal.buttons.cancel')}
          </Button>
          <ButtonWithSpinner variant='contained' type='button' disabled={!selectedContact} onClick={() => handleSaveTicket(selectedContact.id)} color='primary' loading={loading}>
            {i18n.t('newTicketModal.buttons.ok')}
          </ButtonWithSpinner>
        </DialogActions>
        {contactModalOpen && <ContactModal open={contactModalOpen} initialValues={newContact} onClose={handleCloseContactModal} onSave={handleAddNewContactTicket}></ContactModal>}
      </Dialog>

      <TicketAlreadyOpenModal open={openExistingTicketModal} onClose={handleCloseExistingTicketModal} attendingUserName={attendingUserName || existingTicket?.user?.name || 'Nenhum atendente'} />
    </>
  )
}

export default NewTicketModal
